import type { Territory } from "../types"

const STORAGE_KEY = "territory-map-data"
const LAST_MODIFIED_KEY = "territory-map-last-modified"
const MAP_MODE_KEY = "territory-map-mode"
const MAP_TITLE_KEY = "territory-map-title"

export interface StoredMapData {
  territories: Territory[]
  mapMode: "us" | "world"
  mapTitle: string
  lastModified: number
}

export const saveToLocalStorage = (data: StoredMapData): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data.territories))
    localStorage.setItem(MAP_MODE_KEY, data.mapMode)
    localStorage.setItem(MAP_TITLE_KEY, data.mapTitle)
    localStorage.setItem(LAST_MODIFIED_KEY, data.lastModified.toString())
  } catch (error) {
    console.error("Error saving to localStorage:", error)
  }
}

export const loadFromLocalStorage = (): StoredMapData | null => {
  try {
    const territoriesJson = localStorage.getItem(STORAGE_KEY)
    const mapMode = (localStorage.getItem(MAP_MODE_KEY) as "us" | "world") || "us"
    const mapTitle = localStorage.getItem(MAP_TITLE_KEY) || "Coverage Maps Title"
    const lastModifiedStr = localStorage.getItem(LAST_MODIFIED_KEY)

    if (!territoriesJson) return null

    const territories = JSON.parse(territoriesJson) as Territory[]
    const lastModified = lastModifiedStr ? Number.parseInt(lastModifiedStr, 10) : Date.now()

    return {
      territories,
      mapMode,
      mapTitle,
      lastModified,
    }
  } catch (error) {
    console.error("Error loading from localStorage:", error)
    return null
  }
}

export const clearLocalStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(MAP_MODE_KEY)
    localStorage.removeItem(MAP_TITLE_KEY)
    localStorage.removeItem(LAST_MODIFIED_KEY)
  } catch (error) {
    console.error("Error clearing localStorage:", error)
  }
}

export const exportToJson = (data: StoredMapData): string => {
  return JSON.stringify(data, null, 2)
}

export const importFromJson = (jsonString: string): StoredMapData | null => {
  try {
    const data = JSON.parse(jsonString) as StoredMapData

    // Validate the imported data has the required structure
    if (!data.territories || !Array.isArray(data.territories)) {
      throw new Error("Invalid data format: territories array is missing")
    }

    return {
      territories: data.territories,
      mapMode: data.mapMode || "us",
      mapTitle: data.mapTitle || "Coverage Maps Title",
      lastModified: data.lastModified || Date.now(),
    }
  } catch (error) {
    console.error("Error importing from JSON:", error)
    return null
  }
}
